import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { ResponseListsService } from '../../shared/services/response-lists.service';
import { SnackBarService } from '../../core/snack-bar.service';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Column, Type } from '../../campaign/response-lists/response-lists.component';
import { Subscription } from 'rxjs';
import { ThemeService } from '../../shared/services/theme.service';
import { LanguageService } from '../../core/language.service';

@Component({
  selector: 'app-edit-response-list',
  templateUrl: './flyout-edit-response-list.component.html',
  styleUrls: ['./flyout-edit-response-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutEditResponseListComponent implements OnInit, OnDestroy {
  constructor(
    public readonly loggerFactory: LoggerFactory,
    private readonly flyoutService: FlyoutService,
    private readonly responseListsService: ResponseListsService,
    private readonly snackbarService: SnackBarService,
    private readonly themeService: ThemeService,
    private readonly languageService: LanguageService,
  ) {
    this.logger = loggerFactory.get('FlyoutAudienceComponent');
  }

  private readonly logger: Logger;
  public originalName: string;
  public responseListId;
  responseList;
  columns: Column[];
  nameFormControl = new UntypedFormControl('', [Validators.required]);
  loading = false;
  isSelected = false;
  selectionCount = 0;
  public isDarkTheme: boolean;
  private isDarkThemeSub: Subscription;
  private subscriptionArray: Subscription[] = [];
  mergeIntoColumns: Column[];
  mergeFromColumns: Column[];
  mergeFromColumn: Column;
  mergeIntoColumn: Column;
  originalColumns: Column[];
  changedListName = false;
  changedAnyColumnName = false;
  isDeletingColumn = false;

  @Input()
  public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this.responseListId = openingData.responseListId;
  }

  ngOnInit(): void {
    if (this.responseListId) {
      this.loading = true;
      this.GetResponseList();
    } else {
      this.responseList = {};
    }
    this.isDarkThemeSub = this.themeService.isDarkThemeBS.subscribe((item) => {
      this.isDarkTheme = item;
    });
  }

  public GetResponseList() {
    this.responseListsService.getResponseList(this.responseListId).subscribe(
      (response) => {
        this.responseList = response;
        this.originalName = this.responseList.name;
        this.columns = this.responseList.columns;
        this.columns = this.columns.filter((x) => x.key != null && x.key !== undefined && x.key.length > 0);
        this.columns = this.columns.filter((x) => x.type !== Type.Consent);
        this.sortColumns();
        this.originalColumns = JSON.parse(JSON.stringify(this.columns));
        this.mergeIntoColumns = this.columns;
        this.mergeFromColumns = this.columns;
        this.loading = false;
        this.columns.forEach((x) => {
          x['selected'] = false;
        });
        this.originalColumns.forEach((x) => {
          x['selected'] = false;
        });
      },
      (error) => {
        this.logger.error('Could not get responseList ' + JSON.stringify(error));
        this.loading = false;
      },
    );
  }

  public cancelFlyout() {
    this.flyoutService.closeEditResponseListFlyout({
      id: this.responseListId,
      name: this.originalName,
      columns: this.originalColumns,
    });
  }

  public saveResponseList() {
    if (this.responseListId) {
      this.editResponseList();
    } else {
      this.createResponseList();
    }
  }

  editResponseList() {
    this.responseListsService.updateResponseList(this.responseList.id, this.responseList.name).subscribe(
      () => {
        this.responseListsService.updateColumns(this.responseList.id, this.columns, false).subscribe(
          () => {},
          (error) => {
            this.snackbarService.error('response-lists.edit.change-column-error');
            console.error('ERROR UPDATING COLUMN NAME' + JSON.stringify(error));
          },
        );
        this.snackbarService.success('response-lists.edit.save-success');
        this.flyoutService.closeEditResponseListFlyout({
          id: this.responseList.id,
          name: this.responseList.name,
          columns: this.columns,
        });
      },
      (error) => {
        this.snackbarService.error('response-lists.edit.save-error');
        console.error('Error that should break the page, could not edit responselist: ' + JSON.stringify(error));
      },
    );
  }

  createResponseList() {
    this.responseListsService.createResponseList(this.responseList.name).subscribe(
      (responseListId) => {
        this.snackbarService.success('response-lists.edit.save-success');
        this.flyoutService.closeEditResponseListFlyout({
          id: responseListId,
          name: this.responseList.name,
          columns: [],
        });
      },
      (error) => {
        this.snackbarService.error('response-lists.edit.save-error');
        console.error('Error that should break the page, could not create responselist: ' + JSON.stringify(error));
      },
    );
  }

  checkBoxSelected() {
    this.selectionCount = this.columns.filter((x) => x['selected']).length;
    for (let i = 0; i < this.columns.length; i++) {
      this.originalColumns[i]['selected'] = this.columns[i]['selected'];
    }
  }

  deleteSelectedRows() {
    const originalColumnsToSend = this.originalColumns.filter((x) => !x['selected']);
    this.isDeletingColumn = true;
    this.responseListsService.updateColumns(this.responseList.id, originalColumnsToSend, true).subscribe(
      () => {
        this.selectionCount = this.columns.filter((x) => x['selected']).length;
        this.columns = this.columns.filter((x) => !x['selected']);
        this.originalColumns = this.originalColumns.filter((x) => !x['selected']);
        this.columns.forEach((x) => {
          x['selected'] = false;
        });
        this.originalColumns.forEach((x) => {
          x['selected'] = false;
        });
        this.snackbarService.success('response-lists.edit.delete-columns-success');
        this.flyoutService.sendDataEditResponseListFlyout({
          id: this.responseListId,
          name: this.originalName,
          columns: this.originalColumns,
        });
        this.isDeletingColumn = false;
      },
      (error) => {
        this.snackbarService.error('response-lists.edit.delete-columns-error');
        console.error('ERROR UPDATING COLUMN NAME' + JSON.stringify(error));
      },
    );
  }

  private sortColumns() {
    this.columns = this.columns.sort((a, b) => a.key.localeCompare(b.key)).filter((x) => x.key !== 'consent');
    this.columns.sort((a, b) => {
      const aValue = this.getSortValue(a.type);
      const bValue = this.getSortValue(b.type);
      return aValue - bValue;
    });
  }

  private getSortValue(type: Type) {
    switch (type) {
      case Type.Text:
        break;
      case Type.Email:
        return 1;
      case Type.FullName:
        return 4;
      case Type.FirstName:
        return 2;
      case Type.LastName:
        return 3;
      case Type.Phone:
        return 5;
    }
    return 10;
  }

  public disabledInput = {
    [Type.Text]: false,
    [Type.Email]: true,
    [Type.FullName]: true,
    [Type.LastName]: true,
    [Type.FirstName]: true,
    [Type.Phone]: true,
    [Type.RadioButtons]: false,
    [Type.ButtonChoices]: false,
    [Type.Checkboxes]: false,
    [Type.DropDown]: false,
    [Type.Date]: false,
    [Type.CreatedDate]: true,
    [Type.StarRating]: false,
    [Type.SmileyRating]: false,
  };
  public hardCodedNames = {
    [Type.Email]: this.languageService.translateInstantly('response-lists.columns.email'),
    [Type.FullName]: this.languageService.translateInstantly('response-lists.columns.name'),
    [Type.LastName]: this.languageService.translateInstantly('response-lists.columns.lastname'),
    [Type.FirstName]: this.languageService.translateInstantly('response-lists.columns.firstname'),
    [Type.Phone]: this.languageService.translateInstantly('response-lists.columns.phone'),
    [Type.CreatedDate]: this.languageService.translateInstantly('response-lists.columns.created-date'),
  };

  editingName(i: number) {
    if (this.columns[i].name !== this.originalColumns[i].name) {
      this.changedAnyColumnName = true;
    } else {
      this.changedAnyColumnName = false;
    }
  }

  ngOnDestroy() {
    this.subscriptionArray.push(this.isDarkThemeSub);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
